import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash2, Package } from "lucide-react";
import { useDealItems } from "@/hooks/useDealItems";

function formatCurrency(v: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
}

interface DealItemsManagerProps {
  dealId: string;
  workspaceId: string;
}

export function DealItemsManager({ dealId, workspaceId }: DealItemsManagerProps) {
  const { items, addItem, deleteItem } = useDealItems(dealId);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState("");
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");

  const handleAdd = () => {
    if (!name.trim() || !price) return;
    addItem.mutate({
      deal_id: dealId,
      workspace_id: workspaceId,
      product_name_snapshot: name.trim(),
      quantity: parseFloat(qty) || 1,
      unit_price: parseFloat(price) || 0,
    });
    setName("");
    setQty("1");
    setPrice("");
    setShowAdd(false);
  };

  const total = items.reduce((s, i) => s + Number(i.line_total), 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm flex items-center gap-2">
          <Package className="w-4 h-4" />
          Itens ({items.length})
        </h4>
        <Button variant="ghost" size="sm" onClick={() => setShowAdd(!showAdd)}>
          <Plus className="w-4 h-4 mr-1" /> Adicionar
        </Button>
      </div>

      {showAdd && (
        <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
          <div className="space-y-1">
            <Label className="text-xs">Produto</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Nome do produto" className="h-8 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Qtd</Label>
              <Input type="number" min={1} value={qty} onChange={e => setQty(e.target.value)} className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Preço Unit.</Label>
              <Input type="number" min={0} step={0.01} value={price} onChange={e => setPrice(e.target.value)} className="h-8 text-sm" />
            </div>
          </div>
          <Button size="sm" className="w-full" onClick={handleAdd} disabled={addItem.isPending}>Confirmar</Button>
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-1.5">
          {items.map(item => (
            <div key={item.id} className="flex items-center justify-between text-sm px-2 py-1.5 rounded bg-background border">
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium">{item.product_name_snapshot}</p>
                <p className="text-xs text-muted-foreground">{Number(item.quantity)} × {formatCurrency(Number(item.unit_price))}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-xs">{formatCurrency(Number(item.line_total))}</span>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => deleteItem.mutate(item.id)}>
                  <Trash2 className="w-3 h-3 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
          <Separator />
          <div className="flex justify-between font-semibold text-sm px-2">
            <span>Total dos Itens</span>
            <span>{formatCurrency(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
